import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  getDocuments,
  uploadDocument,
  downloadDocument,
  deleteDocument,
  getReferrals,
  getEnrollments,
  isAuthenticated,
  type Document,
} from '@/lib/memberAuth';
import { PageContainer, PageHeader, PageState } from '@/components/weavein';
import {
  Upload,
  FileText,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  File,
  Download,
  Calendar,
  Trash2,
} from 'lucide-react';

export default function MemberDocumentsPage() {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [documents, setDocuments] = useState<Document[]>([]);
  const [referrals, setReferrals] = useState<any[]>([]);
  const [enrollments, setEnrollments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);

  // Upload dialog state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState('');
  const [description, setDescription] = useState('');
  const [linkedReferralId, setLinkedReferralId] = useState<number | undefined>();
  const [linkedEnrollmentId, setLinkedEnrollmentId] = useState<number | undefined>();

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/member/login');
      return;
    }

    loadData();

    // Check if referral_id is in query params (for quick upload)
    const referralId = searchParams.get('referral_id');
    if (referralId) {
      setLinkedReferralId(parseInt(referralId, 10));
      setUploadDialogOpen(true);
    }
  }, [navigate, searchParams]);

  const loadData = async () => {
    setLoading(true);
    setError('');

    try {
      const [docsData, refData, enrollData] = await Promise.all([
        getDocuments(),
        getReferrals().catch(() => []),
        getEnrollments().catch(() => []),
      ]);

      setDocuments(docsData);
      setReferrals(refData);
      setEnrollments(enrollData);
    } catch (err: any) {
      setError(err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !documentType) {
      setError('Please select a file and document type');
      return;
    }

    setUploading(true);
    setError('');

    try {
      await uploadDocument(selectedFile, documentType, {
        description: description || undefined,
        referralId: linkedReferralId,
        enrollmentId: linkedEnrollmentId,
      });

      // Reload documents
      await loadData();

      // Reset form
      setSelectedFile(null);
      setDocumentType('');
      setDescription('');
      setLinkedReferralId(undefined);
      setLinkedEnrollmentId(undefined);
      setUploadDialogOpen(false);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err: any) {
      setError(err.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleOpenUploadDialog = () => {
    setSelectedFile(null);
    setDocumentType('');
    setDescription('');
    setLinkedReferralId(undefined);
    setLinkedEnrollmentId(undefined);
    setUploadDialogOpen(true);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getDocumentIcon = (mimeType: string | null | undefined) => {
    if (!mimeType) return '📎';
    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType.includes('sheet')) return '📊';
    return '📎';
  };

  const handleDownload = async (doc: Document) => {
    try {
      const blobUrl = await downloadDocument(doc.id);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = doc.document_name || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err: any) {
      setError(err.message || 'Failed to download document');
    }
  };

  const handleDelete = async (doc: Document) => {
    if (!window.confirm(`Delete "${doc.document_name}"? This cannot be undone.`)) return;
    try {
      await deleteDocument(doc.id);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete document');
    }
  };

  const header = (
    <PageHeader
      leading={
        <Link to="/member/dashboard">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('documents.back', 'Back')}
          </Button>
        </Link>
      }
      title={t('documents.my_documents', 'My Documents')}
      description={<>{documents.length} document{documents.length !== 1 ? 's' : ''}</>}
      actions={
        <Button onClick={handleOpenUploadDialog}>
          <Upload className="w-4 h-4 mr-2" />
          {t('documents.upload_document', 'Upload Document')}
        </Button>
      }
    />
  );

  if (loading) {
    return (
      <>
        {header}
        <PageContainer size="wide">
          <PageState variant="loading" />
        </PageContainer>
      </>
    );
  }

  return (
    <>
      {header}
      <PageContainer size="wide">
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {documents.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-ink-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-ink mb-2">{t('documents.no_documents_yet', 'No documents yet')}</h3>
              <p className="text-ink-500 mb-6">
                {t('documents.upload_documents_to_share_with', 'Upload documents to share with your case managers or keep for your records.')}
              </p>
              <Button
                onClick={handleOpenUploadDialog}
                className="bg-v2accent hover:bg-v2accent/90"
              >
                <Upload className="w-4 h-4 mr-2" />
                {t('documents.upload_your_first_document', 'Upload Your First Document')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {documents.map((doc) => (
              <Card key={doc.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className="text-3xl">{getDocumentIcon(doc.mime_type)}</div>
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-lg truncate">
                          {doc.document_name}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          {doc.document_type.replace(/_/g, ' ').toUpperCase()}
                        </CardDescription>
                      </div>
                    </div>
                    {doc.is_verified && (
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {doc.description && (
                    <p className="text-sm text-ink-700">{doc.description}</p>
                  )}

                  <div className="flex items-center justify-between text-sm text-ink-500">
                    <span>{formatFileSize(doc.file_size_bytes)}</span>
                    <span className="flex items-center">
                      <Calendar className="w-3 h-3 mr-1" />
                      {formatDate(doc.created_at)}
                    </span>
                  </div>

                  {(doc.referral_id || doc.enrollment_id) && (
                    <div className="pt-3 border-t">
                      <p className="text-xs text-ink-500">
                        Linked to:{' '}
                        {doc.referral_id && (
                          <span className="font-medium">Referral #{doc.referral_id}</span>
                        )}
                        {doc.enrollment_id && (
                          <span className="font-medium">Enrollment #{doc.enrollment_id}</span>
                        )}
                      </p>
                    </div>
                  )}

                  <div className="flex items-center space-x-2 pt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => handleDownload(doc)}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {t('documents.download', 'Download')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(doc)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </PageContainer>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('documents.upload_document_1', 'Upload Document')}</DialogTitle>
            <DialogDescription>
              {t('documents.upload_a_document_to_share', 'Upload a document to share with your case managers or keep for your records.')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* File Input */}
            <div>
              <Label htmlFor="file">{t('documents.select_file', 'Select File')}</Label>
              <Input
                id="file"
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                disabled={uploading}
                className="mt-2"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.xls,.xlsx"
              />
              {selectedFile && (
                <p className="text-sm text-ink-500 mt-2">
                  Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                </p>
              )}
              <p className="text-xs text-ink-500 mt-2">
                {t('documents.maximum_file_size_10mb_supported', 'Maximum file size: 10MB. Supported formats: PDF, Word, Excel, Images')}
              </p>
            </div>

            {/* Document Type */}
            <div>
              <Label htmlFor="documentType">{t('documents.document_type', 'Document Type *')}</Label>
              <Select value={documentType} onValueChange={setDocumentType} disabled={uploading}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder={t('documents.select_document_type', 'Select document type')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="medical_record">{t('documents.medical_record', 'Medical Record')}</SelectItem>
                  <SelectItem value="insurance_card">{t('documents.insurance_card', 'Insurance Card')}</SelectItem>
                  <SelectItem value="identification">{t('documents.identification', 'Identification')}</SelectItem>
                  <SelectItem value="consent_form">{t('documents.consent_form', 'Consent Form')}</SelectItem>
                  <SelectItem value="lab_result">{t('documents.lab_result', 'Lab Result')}</SelectItem>
                  <SelectItem value="prescription">{t('documents.prescription', 'Prescription')}</SelectItem>
                  <SelectItem value="financial_document">{t('documents.financial_document', 'Financial Document')}</SelectItem>
                  <SelectItem value="other">{t('documents.other', 'Other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                placeholder={t('documents.add_a_description_or_notes', 'Add a description or notes about this document...')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={uploading}
                rows={3}
                className="mt-2"
              />
            </div>

            {/* Link to Referral */}
            {referrals.length > 0 && (
              <div>
                <Label htmlFor="referral">Link to Referral (Optional)</Label>
                <Select
                  value={linkedReferralId?.toString() || 'none'}
                  onValueChange={(val) =>
                    setLinkedReferralId(val === 'none' ? undefined : parseInt(val, 10))
                  }
                  disabled={uploading}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder={t('documents.select_referral', 'Select referral')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('documents.none', 'None')}</SelectItem>
                    {referrals.map((ref) => (
                      <SelectItem key={ref.id} value={ref.id.toString()}>
                        Referral #{ref.referral_number} - {ref.program_name || 'General'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Link to Enrollment */}
            {enrollments.length > 0 && (
              <div>
                <Label htmlFor="enrollment">Link to Enrollment (Optional)</Label>
                <Select
                  value={linkedEnrollmentId?.toString() || 'none'}
                  onValueChange={(val) =>
                    setLinkedEnrollmentId(val === 'none' ? undefined : parseInt(val, 10))
                  }
                  disabled={uploading}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder={t('documents.select_enrollment', 'Select enrollment')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('documents.none_1', 'None')}</SelectItem>
                    {enrollments.map((enrollment) => (
                      <SelectItem key={enrollment.id} value={enrollment.id.toString()}>
                        {enrollment.program_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setUploadDialogOpen(false)}
              disabled={uploading}
            >
              {t('documents.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleUpload}
              disabled={uploading || !selectedFile || !documentType}
              className="bg-v2accent hover:bg-v2accent/90"
            >
              {uploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  {t('documents.uploading', 'Uploading...')}
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  {t('documents.upload_document_2', 'Upload Document')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
